import { head, pipe, pluck, propEq, values } from 'ramda'
import ApiClient from 'api-client/ApiClient'
import ApiService from 'api-client/ApiService'
import { generateApiErrorPayload } from 'api-client/helpers'
import { loginUrl } from 'app/constants'
import { isSystemUser } from 'app/core/utils/helpers'
import type { GroupsResponse } from 'app/plugins/account/components/ssoManagement/groups/model'
import type { Domain } from 'app/plugins/account/components/userManagement/domains/model'
import { filterValidTenants } from 'app/plugins/account/components/userManagement/tenants/helpers'
import { filterValidUsers } from 'app/plugins/account/components/userManagement/users/helpers'
import store from 'app/store'
import config from 'app-config'
import ApiError from 'core/errors/ApiError'
import { getUserPrefsBucket, preferencesStoreKey } from 'core/session/preferencesReducers'
import { sessionActions } from 'core/session/sessionReducers'
import DataKeys from 'k8s/DataKeys'
import { pathStr } from 'utils/fp'
import { pathJoin } from 'utils/misc'
import {
  addDomainIdToURL,
  getHighestRole,
  getIDPName,
  groupByRegion,
  trackApiMethodMetadata,
} from './helpers'
import type {
  AuthToken,
  CqrsRole,
  CreateDomain,
  CreateDomainResponse,
  GetApplicationCredentials,
  GetCapabilitiesListResponse,
  GetCqrsRoleResponse,
  GetCredentials,
  GetDomainResponse,
  GetFeatureLinks,
  GetFeatures,
  GetPermissionsListResponse,
  GetRegions,
  GetRoles,
  GetServiceCatalog,
  GetUserRoleAssignments,
  GetUsers,
  ServicesByName,
  ServicesByRegion,
  UpdateProject,
  UpdateUser,
  UserPermissionsMap,
} from './keystone.model'

const { dispatch } = store

const constructAuthFromToken = (token: string, projectId?: string) => {
  return {
    auth: {
      ...(projectId ? { scope: { project: { id: projectId } } } : {}),
      identity: {
        methods: ['token'],
        token: { id: token },
      },
    },
  }
}

const constructAuthFromSaml = (token: string, projectId?: string) => {
  return {
    auth: {
      identity: {
        methods: ['saml2'],
        saml2: {
          id: token,
        },
      },
      scope: {
        project: {
          id: projectId,
        },
      },
    },
  }
}

const constructAuthFromCredentials = (username, password, domain) => {
  return {
    auth: {
      identity: {
        methods: ['password'],
        password: {
          user: {
            name: username,
            domain: domain ? { name: domain } : { id: 'default' },
            password,
          },
        },
      },
    },
  }
}

const constructAuthFromCredentialsWithProjectId = (username, password, projectId) => {
  return {
    auth: {
      identity: {
        methods: ['password'],
        password: {
          user: {
            name: username,
            domain: { id: 'default' },
            password,
          },
        },
      },
      scope: {
        project: {
          id: projectId,
        },
      },
    },
  }
}

const constructAuthFromCredentialsTotp = (username, password, totp, domain) => {
  return {
    auth: {
      identity: {
        methods: ['password', 'totp'],
        password: {
          user: {
            name: username,
            domain: domain ? { name: domain } : { id: 'default' },
            password,
          },
        },
        totp: {
          user: {
            name: username,
            domain: domain ? { name: domain } : { id: 'default' },
            passcode: totp,
          },
        },
      },
    },
  }
}

class Keystone extends ApiService {
  public getClassName() {
    return 'keystone'
  }

  static apiMethodsMetadata = []

  protected async getEndpoint() {
    return Promise.resolve(this.client.options.keystoneEndpoint)
  }

  /**
   * Returns the base URL for the Neutron API
   */
  get baseRbacEndpoint(): string {
    if (location.hostname === 'localhost') {
      return `${config.apiHost}/pf9/authz`
    }

    return `${location.origin}/pf9/authz`
  }

  get v3() {
    return `/v3`
  }

  get adminV3() {
    return `/v3`
  }

  get catalogUrl() {
    return `${this.v3}/auth/catalog`
  }

  get projectsAuthUrl() {
    return `${this.v3}/auth/projects`
  }

  get endpointsUrl() {
    return `${this.v3}/endpoints`
  }

  get regionsUrl() {
    return `${this.v3}/regions`
  }

  get projectsUrl() {
    return `${this.v3}/projects`
  }

  get domainsAuthUrl() {
    return `${this.v3}/auth/domains`
  }

  get domainsUrl() {
    return `${this.v3}/domains`
  }

  get allTenantsAllUsersUrl() {
    return `${this.adminV3}/PF9-KSADM/all_tenants_all_users`
  }

  get roleAssignments() {
    return `${this.adminV3}/role_assignments`
  }

  get tokensUrl() {
    return `${this.v3}/auth/tokens?nocatalog`
  }

  get tokensUrlWithCatalog() {
    return `${this.v3}/auth/tokens`
  }

  get identityProvidersUrl() {
    return `${this.v3}/OS-FEDERATION/identity_providers`
  }

  get ssoLoginUrl() {
    return `${this.v3}/OS-FEDERATION/identity_providers/domain_name/protocols/saml2/auth`
  }

  get usersUrl() {
    return `${this.v3}/users`
  }

  get credentialsUrl() {
    return `${this.v3}/credentials`
  }

  get groupsUrl() {
    return `${this.v3}/groups`
  }

  get groupMappingsUrl() {
    return `${this.v3}/OS-FEDERATION/mappings`
  }

  get rolesUrl() {
    return `${this.v3}/roles`
  }

  get applicationCredentialsUrl() {
    return `${this.v3}/users/:userId/application_credentials`
  }

  checkServiceHealth = async (url: string = '/v3', endpoint: string = '') => {
    return this.client.basicGet<any>({
      url,
      endpoint: endpoint ? endpoint : this.client.options.keystoneEndpoint,
      options: {
        clsName: this.getClassName(),
        mthdName: 'checkServiceHealth',
      },
    })
  }

  @trackApiMethodMetadata({ url: '/v3/projects/:projectId', type: 'GET', params: ['projectId'] })
  getProject = async (id) => {
    const data = await this.client.basicGet<any>({
      url: `${this.projectsUrl}/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getProject',
      },
    })
    return data.project
  }

  @trackApiMethodMetadata({ url: '/v3/auth/projects', type: 'GET' })
  getProjectsAuth = async () => {
    const response = await this.client.rawGet<any>({
      url: addDomainIdToURL(this.projectsAuthUrl),
      config: this.client.getAuthHeaders({ scoped: false }),
      options: {
        clsName: this.getClassName(),
        mthdName: 'getProjectsAuth',
      },
    })

    // Filter the internal tenants at source
    return response.data.projects?.filter(filterValidTenants) || []
  }

  @trackApiMethodMetadata({ url: '/v3/projects?domain_id={domainId}', type: 'GET' })
  getProjects = async (domainId?: string) => {
    // /v3/projects requires a scoped token (as of the 2026.8 release)
    const response = await this.client.rawGet<any>({
      url: addDomainIdToURL(this.projectsUrl, domainId),
      config: this.client.getAuthHeaders(),
      options: {
        clsName: this.getClassName(),
        mthdName: 'getProjects',
      },
    })

    // Filter the internal tenants at source
    return response?.data?.projects?.filter(filterValidTenants) || []
  }

  @trackApiMethodMetadata({ url: '/v3/projects', type: 'GET' })
  getAllDomainProjects = async () => {
    // /v3/projects requires a scoped token (as of the 2026.8 release)
    const response = await this.client.rawGet<any>({
      url: this.projectsUrl,
      config: this.client.getAuthHeaders(),
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllDomainProjects',
      },
    })

    // Filter the internal tenants at source
    return response?.data?.projects?.filter(filterValidTenants) || []
  }

  @trackApiMethodMetadata({ url: '/v3/auth/domains', type: 'GET' })
  getRawDomains = async (scoped = false) => {
    const response = await this.client.rawGet<GetDomainResponse>({
      url: this.domainsAuthUrl,
      config: this.client.getAuthHeaders({ scoped }),
      options: {
        clsName: this.getClassName(),
        mthdName: 'getDomains',
      },
    })
    return response.data.domains
  }

  @trackApiMethodMetadata({ url: '/v3/domains', type: 'GET' })
  getDomains = async () => {
    const response = await this.client.basicGet<GetDomainResponse>({
      url: this.domainsUrl,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getDomains',
      },
    })
    return response.domains
  }

  @trackApiMethodMetadata({ url: '/v3/domains', type: 'POST' })
  createDomain = async (body: CreateDomain) => {
    const response = await this.client.basicPost<CreateDomainResponse>({
      url: this.domainsUrl,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createDomain',
      },
    })
    return response.domain
  }

  @trackApiMethodMetadata({ url: '/v3/domains/:id', type: 'PUT', params: ['id'] })
  updateDomain = async (id: string, body: CreateDomain) => {
    const response = await this.client.basicPatch<CreateDomainResponse>({
      url: `${this.domainsUrl}/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateDomain',
      },
    })
    return response.domain
  }

  @trackApiMethodMetadata({ url: '/v3/domains/:id', type: 'DELETE', params: ['id'] })
  deleteDomain = async (body: Domain) => {
    return this.client.basicDelete<any>({
      url: `${this.domainsUrl}/${body.id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteDomain',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/v3/role_assignments?scope.project.id=:tenantId',
    type: 'GET',
    params: ['tenantId', 'effective'],
  })
  getTenantRoleAssignments = async (tenantId, effective = false) => {
    const data = await this.client.basicGet<GetUserRoleAssignments>({
      url: this.roleAssignments,
      params: {
        'scope.project.id': tenantId,
        include_names: true,
        // effective=false made it act as if true, need undefined
        effective: effective ? true : undefined,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'getTenantRoleAssignments',
      },
    })
    return data.role_assignments
  }

  @trackApiMethodMetadata({
    url: '/v3/role_assignments',
    type: 'GET',
    params: ['user.id', 'include_names', 'effective'],
  })
  getUserRoleAssignments = async (userId, effective = false) => {
    const data = await this.client.basicGet<GetUserRoleAssignments>({
      url: this.roleAssignments,
      params: {
        'user.id': userId,
        include_names: true,
        effective: effective ? true : undefined,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'getUserRoleAssignments',
      },
    })
    return data.role_assignments
  }

  @trackApiMethodMetadata({
    url: '/v3/role_assignments',
    type: 'GET',
    params: ['group.id', 'include_names'],
  })
  getGroupRoleAssignments = async (groupId) => {
    // Todo: typings for this API
    const data = await this.client.basicGet<any>({
      url: this.roleAssignments,
      params: {
        'group.id': groupId,
        include_names: true,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'getGroupRoleAssignments',
      },
    })
    return data.role_assignments
  }

  @trackApiMethodMetadata({
    url: '/v3/projects/:tenantId/users/:userId/roles/:roleId',
    type: 'PUT',
    params: ['tenantId', 'userId', 'roleId'],
  })
  addUserRole = async ({ tenantId, userId, roleId }) => {
    await this.client.basicPut<string>({
      url: pathJoin(this.projectsUrl, `${tenantId}/users/${userId}/roles/${roleId}`),
      body: {},
      options: {
        clsName: this.getClassName(),
        mthdName: 'addUserRole',
      },
    })
    return { tenantId, userId, roleId }
  }

  @trackApiMethodMetadata({
    url: '/v3/projects/:tenantId/users/:userId/roles/:roleId',
    type: 'DELETE',
    params: ['tenantId', 'userId', 'roleId'],
  })
  deleteUserRole = async ({ tenantId, userId, roleId }) => {
    try {
      await this.client.basicDelete<any>({
        url: pathJoin(this.projectsUrl, `${tenantId}/users/${userId}/roles/${roleId}`),
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteUserRole',
        },
      })
      return { tenantId, userId, roleId }
    } catch (err) {
      throw new ApiError('Unable to delete non-existant project')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/role_assignments?scope.domain.id=:domainId',
    type: 'GET',
    params: ['domainId'],
  })
  getDomainRoleAssignments = async (domainId) => {
    const data = await this.client.basicGet<any>({
      url: this.roleAssignments,
      params: {
        'scope.domain.id': domainId,
        include_names: true,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'getDomainRoleAssignments',
      },
    })
    return data.role_assignments
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-INHERIT/domains/:domainId/users/:userId/roles/:roleId/inherited_to_projects',
    type: 'PUT',
    params: ['domainId', 'userId', 'roleId'],
  })
  addDomainUserRole = async ({ domainId, userId, roleId }) => {
    await this.client.basicPut<string>({
      url: `/v3/OS-INHERIT/domains/${domainId}/users/${userId}/roles/${roleId}/inherited_to_projects`,
      body: {},
      options: {
        clsName: this.getClassName(),
        mthdName: 'addDomainUserRole',
      },
    })
    return { domainId, userId, roleId }
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-INHERIT/domains/:domainId/users/:userId/roles/:roleId/inherited_to_projects',
    type: 'DELETE',
    params: ['domainId', 'userId', 'roleId'],
  })
  deleteDomainUserRole = async ({ domainId, userId, roleId }) => {
    try {
      await this.client.basicDelete<any>({
        url: `/v3/OS-INHERIT/domains/${domainId}/users/${userId}/roles/${roleId}/inherited_to_projects`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteDomainUserRole',
        },
      })
      return { domainId, userId, roleId }
    } catch (err) {
      throw new ApiError('Unable to revoke domain role')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-INHERIT/domains/:domainId/groups/:groupId/roles/:roleId/inherited_to_projects',
    type: 'PUT',
    params: ['domainId', 'groupId', 'roleId'],
  })
  addDomainGroupRole = async ({ domainId, groupId, roleId }) => {
    try {
      await this.client.basicPut<string>({
        url: `/v3/OS-INHERIT/domains/${domainId}/groups/${groupId}/roles/${roleId}/inherited_to_projects`,
        body: {},
        options: {
          clsName: this.getClassName(),
          mthdName: 'addDomainGroupRole',
        },
      })
      return { domainId, groupId, roleId }
    } catch (err) {
      throw new ApiError('Failed to add domain role to group')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-INHERIT/domains/:domainId/groups/:groupId/roles/:roleId/inherited_to_projects',
    type: 'DELETE',
    params: ['domainId', 'groupId', 'roleId'],
  })
  deleteDomainGroupRole = async ({ domainId, groupId, roleId }) => {
    try {
      await this.client.basicDelete<any>({
        url: `/v3/OS-INHERIT/domains/${domainId}/groups/${groupId}/roles/${roleId}/inherited_to_projects`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteDomainGroupRole',
        },
      })
      return { domainId, groupId, roleId }
    } catch (err) {
      throw new ApiError('Unable to revoke domain role for group')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/system/users/:userId/roles',
    type: 'GET',
    params: ['userId'],
  })
  getUserSystemRoleAssignments = async (userId) => {
    const data = await this.client.basicGet<any>({
      url: `/v3/system/users/${userId}/roles`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getUserSystemRoleAssignments',
      },
    })
    return data.roles
  }

  @trackApiMethodMetadata({
    url: '/v3/system/users/:userId/roles/:roleId',
    type: 'PUT',
    params: ['userId', 'roleId'],
  })
  addSystemUserRole = async ({ userId, roleId }) => {
    await this.client.basicPut<string>({
      url: `/v3/system/users/${userId}/roles/${roleId}`,
      body: {},
      options: {
        clsName: this.getClassName(),
        mthdName: 'addSystemUserRole',
      },
    })
    return { userId, roleId }
  }

  @trackApiMethodMetadata({
    url: '/v3/system/users/:userId/roles/:roleId',
    type: 'DELETE',
    params: ['userId', 'roleId'],
  })
  deleteSystemUserRole = async ({ userId, roleId }) => {
    try {
      await this.client.basicDelete<any>({
        url: `/v3/system/users/${userId}/roles/${roleId}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteSystemUserRole',
        },
      })
      return { userId, roleId }
    } catch (err) {
      throw new ApiError('Unable to revoke system role')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/system/groups/:groupId/roles',
    type: 'GET',
    params: ['groupId'],
  })
  getGroupSystemRoleAssignments = async (groupId) => {
    try {
      const data = await this.client.basicGet<any>({
        url: `/v3/system/groups/${groupId}/roles`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getGroupSystemRoleAssignments',
        },
      })
      return data.roles
    } catch (err) {
      throw new ApiError('Failed to get system role assignments for group')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/system/groups/:groupId/roles/:roleId',
    type: 'PUT',
    params: ['groupId', 'roleId'],
  })
  addSystemGroupRole = async ({ groupId, roleId }) => {
    try {
      await this.client.basicPut<string>({
        url: `/v3/system/groups/${groupId}/roles/${roleId}`,
        body: {},
        options: {
          clsName: this.getClassName(),
          mthdName: 'addSystemGroupRole',
        },
      })
      return { groupId, roleId }
    } catch (err) {
      throw new ApiError('Failed to add system role to group')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/system/groups/:groupId/roles/:roleId',
    type: 'DELETE',
    params: ['groupId', 'roleId'],
  })
  deleteSystemGroupRole = async ({ groupId, roleId }) => {
    try {
      await this.client.basicDelete<any>({
        url: `/v3/system/groups/${groupId}/roles/${roleId}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteSystemGroupRole',
        },
      })
      return { groupId, roleId }
    } catch (err) {
      throw new ApiError('Unable to revoke system role for group')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-FEDERATION/identity_providers',
    type: 'GET',
  })
  getIdentityProviders = async () => {
    const data = await this.client.basicGet<any>({
      url: `${this.identityProvidersUrl}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getIdentityProviders',
      },
    })
    return data.identity_providers
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-FEDERATION/identity_providers',
    type: 'PUT',
  })
  addIdentityProvider = async (body) => {
    const data = await this.client.basicPut<any>({
      url: `${this.identityProvidersUrl}/${getIDPName()}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addIdentityProvider',
      },
    })
    return data.identity_provider
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-FEDERATION/identity_providers',
    type: 'DELETE',
  })
  deleteIdentityProvider = async () => {
    try {
      return await this.client.basicDelete<any>({
        url: `${this.identityProvidersUrl}/${getIDPName()}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteIdentityProvider',
        },
      })
    } catch (err) {
      throw new ApiError(err?.err?.message || err?.message || 'Failed to delete identity provider')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-FEDERATION/identity_providers/domain_name',
    type: 'PATCH',
  })
  updateIdentityProvider = async (body) => {
    const data = await this.client.basicPatch<any>({
      url: `${this.identityProvidersUrl}/${getIDPName()}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateIdentityProvider',
      },
    })
    return data.identity_provider
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-FEDERATION/identity_providers/domain_name/protocols',
    type: 'GET',
  })
  getIdpProtocols = async () => {
    const data = await this.client.basicGet<any>({
      url: `${this.identityProvidersUrl}/${getIDPName()}/protocols`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getIdpProtocols',
      },
    })
    return data.protocols
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-FEDERATION/identity_providers/domain_name/protocols/saml2',
    type: 'PUT',
  })
  addIdpProtocol = async (mappingId) => {
    const body = {
      protocol: {
        mapping_id: mappingId,
      },
    }
    const data = await this.client.basicPut<any>({
      url: `${this.identityProvidersUrl}/${getIDPName()}/protocols/saml2`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addIdpProtocol',
      },
    })
    return data.protocol
  }

  @trackApiMethodMetadata({ url: '/v3/groups', type: 'GET' })
  getGroups = async ({ domainId = undefined }) => {
    const data = await this.client.basicGet<GroupsResponse>({
      url: addDomainIdToURL(this.groupsUrl, domainId),
      options: {
        clsName: this.getClassName(),
        mthdName: 'getGroups',
      },
    })
    return data.groups
  }

  @trackApiMethodMetadata({ url: '/v3/groups', type: 'POST' })
  createGroup = async (params) => {
    const body = { group: params }
    const data = await this.client.basicPost<any>({
      url: addDomainIdToURL(this.groupsUrl),
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createGroup',
      },
    })
    return data.group
  }

  @trackApiMethodMetadata({ url: '/v3/groups/:id', type: 'PATCH', params: ['id'] })
  updateGroup = async (id, params) => {
    const body = { group: params }
    const url = `${this.groupsUrl}/${id}`
    const data = await this.client.basicPatch<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateGroup',
      },
    })
    return data.group
  }

  @trackApiMethodMetadata({ url: '/v3/groups/:groupId', type: 'DELETE', params: ['groupId'] })
  deleteGroup = async (groupId) => {
    try {
      await this.client.basicDelete<any>({
        url: `${this.groupsUrl}/${groupId}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteGroup',
        },
      })
      return groupId
    } catch (err) {
      throw new ApiError(err?.err?.message || err?.message || 'Unable to delete non-existant group')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/groups/:groupId/users/:userId',
    type: 'PUT',
    params: ['groupId', 'userId'],
  })
  addGroupUser = async ({ groupId, userId }) => {
    return await this.client.basicPut<any>({
      url: `${this.groupsUrl}/${groupId}/users/${userId}`,
      body: {},
      options: {
        clsName: this.getClassName(),
        mthdName: 'addGroupUser',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/v3/groups/:groupId/users/:userId',
    type: 'DELETE',
    params: ['groupId', 'userId'],
  })
  removeGroupUser = async ({ groupId, userId }) => {
    return await this.client.basicDelete<any>({
      url: `${this.groupsUrl}/${groupId}/users/${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeGroupUser',
      },
    })
  }

  @trackApiMethodMetadata({ url: '/v3/groups/:groupId/users', type: 'GET', params: ['groupId'] })
  getGroupUsers = async ({ groupId }) => {
    const response = await this.client.basicGet<any>({
      url: `${this.groupsUrl}/${groupId}/users`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getGroupUsers',
      },
    })
    return response?.users
  }

  @trackApiMethodMetadata({
    url: '/v3/projects/:tenantId/groups/:groupId/roles/:roleId',
    type: 'PUT',
    params: ['tenantId', 'groupId', 'roleId'],
  })
  addGroupRole = async ({ tenantId, groupId, roleId }) => {
    await this.client.basicPut<string>({
      url: pathJoin(this.projectsUrl, `${tenantId}/groups/${groupId}/roles/${roleId}`),
      body: {},
      options: {
        clsName: this.getClassName(),
        mthdName: 'addGroupRole',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/v3/projects/:tenantId/groups/:groupId/roles/:roleId',
    type: 'DELETE',
    params: ['tenantId', 'groupId', 'roleId'],
  })
  deleteGroupRole = async ({ tenantId, groupId, roleId }) => {
    try {
      await this.client.basicDelete<any>({
        url: pathJoin(this.projectsUrl, `${tenantId}/groups/${groupId}/roles/${roleId}`),
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteGroupRole',
        },
      })
      return { tenantId, groupId, roleId }
    } catch (err) {
      throw new ApiError('Unable to delete non-existant role')
    }
  }

  @trackApiMethodMetadata({ url: '/v3/OS-FEDERATION/mappings', type: 'GET' })
  getGroupMappings = async () => {
    const data = await this.client.basicGet<any>({
      url: this.groupMappingsUrl,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getGroupMappings',
      },
    })
    return data?.mappings || []
  }

  @trackApiMethodMetadata({ url: '/v3/OS-FEDERATION/mappings/:id', type: 'PUT', params: ['id'] })
  createGroupMapping = async (id, params) => {
    const body = { mapping: params }
    const data = await this.client.basicPut<any>({
      url: `${this.groupMappingsUrl}/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createGroupMapping',
      },
    })
    return data.mapping
  }

  @trackApiMethodMetadata({ url: '/v3/OS-FEDERATION/mappings/:id', type: 'PATCH', params: ['id'] })
  updateGroupMapping = async (id, params) => {
    const body = { mapping: params }
    const url = `${this.groupMappingsUrl}/${id}`
    const data = await this.client.basicPatch<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateGroupMapping',
      },
    })
    return data.mapping
  }

  @trackApiMethodMetadata({
    url: '/v3/OS-FEDERATION/mappings/:id',
    type: 'DELETE',
    params: ['id'],
  })
  deleteGroupMapping = async (mappingId) => {
    try {
      await this.client.basicDelete<any>({
        url: `${this.groupMappingsUrl}/${mappingId}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteGroupMapping',
        },
      })
      return mappingId
    } catch (err) {
      throw new ApiError(
        err?.err?.message || err?.message || 'Unable to delete non-existant group mapping',
      )
    }
  }

  @trackApiMethodMetadata({ url: '/v3/roles', type: 'GET' })
  getRoles = async () => {
    const data = await this.client.basicGet<GetRoles>({
      url: this.rolesUrl,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getRoles',
      },
    })
    return data.roles
  }

  @trackApiMethodMetadata({ url: '/v3/roles/:id', type: 'GET' })
  getRole = async ({ id }) => {
    const data = await this.client.basicGet<any>({
      url: `/v3/roles/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getRole',
      },
    })
    return data.role
  }

  @trackApiMethodMetadata({ url: '/v3/projects', type: 'POST' })
  createProject = async (params) => {
    const body = { project: params }
    const data = await this.client.basicPost<any>({
      url: addDomainIdToURL(this.projectsUrl),
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createProject',
      },
    })
    return data.project
  }

  @trackApiMethodMetadata({ url: '/v3/projects/:projectId', type: 'PATCH', params: ['projectId'] })
  updateProject = async (id, params) => {
    const body = { project: params }
    const url = `${this.projectsUrl}/${id}`
    const data = await this.client.basicPatch<UpdateProject>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateProject',
      },
    })
    return data.project
  }

  @trackApiMethodMetadata({
    url: '/v3/projects/:projectId',
    type: 'DELETE',
    params: ['projectId'],
  })
  deleteProject = async (projectId) => {
    try {
      await this.client.basicDelete<any>({
        url: `${this.projectsUrl}/${projectId}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteProject',
        },
      })
      return projectId
    } catch (err) {
      throw new ApiError('Unable to delete non-existant project')
    }
  }

  changeProjectScopeWithToken = async (projectId, isSsoToken) => {
    const body = isSsoToken
      ? constructAuthFromSaml(this.client.unscopedToken, projectId)
      : constructAuthFromToken(this.client.unscopedToken, projectId)
    return this.changeProjectScope(projectId, body, 'changeProjectScopeWithToken')
  }

  changeProjectScopeWithCredentials = async (username, password, projectId) => {
    const body = constructAuthFromCredentialsWithProjectId(username, password, projectId)
    return this.changeProjectScope(projectId, body, 'changeProjectScopeWithCredentials')
  }

  getSystemToken = async (unscopedToken, domainId, currentTenantId) => {
    const response = await this.client.rawPost<any>({
      url: this.tokensUrlWithCatalog,
      data: {
        auth: {
          identity: {
            methods: ['token'],
            token: {
              id: unscopedToken,
            },
          },
          scope: {
            system: {
              all: true,
            },
          },
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSystemToken',
      },
    })
    const scopedToken = response.headers['x-subject-token']
    const roles = response?.data?.token?.roles || [] // pathStr('data.token.roles', response) as Array<{ [key: string]: any }>
    // @ts-ignore
    const roleNames = pluck('name', roles)
    const role = getHighestRole(roleNames)
    const _user = pathStr('data.token.user', response)
    this.client.scopedToken = scopedToken
    // Use the domain UUID from the token (guaranteed correct). The `domainId`
    // parameter is a domain NAME string; OpenStack /v3/projects?domain_id= expects
    // a UUID. The default domain works by coincidence (name === UUID === "default"),
    // but non-default domains return empty results when the name is passed — causing
    // activeProjectId to be undefined. (PCD-9529)
    const tokenDomainId = response?.data?.token?.user?.domain?.id || domainId
    const projectsResponse = await this.client.rawGet<any>({
      url: addDomainIdToURL(this.projectsUrl, tokenDomainId),
      config: {
        headers: {
          'X-Auth-Token': scopedToken,
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'getProjects',
      },
    })
    const projects = projectsResponse?.data?.projects || []
    const currentIsValid = projects?.some((t) => t?.id === currentTenantId)
    const defaultProject = projects?.find(propEq('name', 'service')) || head(projects)
    this.client.activeProjectId = currentIsValid ? currentTenantId : defaultProject?.id
    // Assign service catalog straight from token bc
    // system user not allowed to access service catalog API
    this.client.serviceCatalog = response?.data?.token?.catalog
    // Store accessible app catalog for API Access Page
    dispatch(
      sessionActions.updateSession({
        serviceCatalog: response?.data?.token?.catalog,
      }),
    )
    const user = await this.getUser(_user.id)
    return {
      user: {
        ...user,
        email: user.email || user.name,
        userId: user.id,
        role: role,
        displayName: user.displayname || user.name,
      },
      role,
      domain: response?.data?.token?.user?.domain || null,
      scopedToken,
    }
  }

  changeProjectScope = async (projectId, body, mthdName) => {
    if (isSystemUser()) {
      // Same system token is used across all tenants/domains, so skip the
      // token re-scoping POST below and return the existing session's
      // user/role/token unchanged.
      this.client.activeProjectId = projectId
      await ApiClient.refreshApiEndpoints()
      const { userDetails = {}, scopedToken, domain } = store.getState()?.session || {}
      return {
        user: userDetails,
        role: userDetails?.role,
        domain: domain || null,
        scopedToken,
      }
    }
    try {
      const response = await this.client.rawPost<AuthToken>({
        url: this.tokensUrl,
        data: body,
        options: {
          clsName: this.getClassName(),
          mthdName,
        },
      })
      const scopedToken = response.headers['x-subject-token']
      // FIXME: fix typings here
      const roles = response?.data?.token?.roles || [] // pathStr('data.token.roles', response) as Array<{ [key: string]: any }>
      const roleNames = pluck('name', roles)
      const role = getHighestRole(roleNames)
      const _user = pathStr('data.token.user', response)
      // Extra properties in user are required to ensure
      // functionality in the old UI
      this.client.activeProjectId = projectId
      this.client.scopedToken = scopedToken
      this.client.serviceCatalog = null // reinitialize the serviceCatalog before fetching again
      await this.getServiceCatalog()
      const user = await this.getUser(_user.id)

      await ApiClient.refreshApiEndpoints()

      return {
        user: {
          ...user,
          email: user.email || user.name,
          userId: user.id,
          role: role,
          displayName: user.displayname || user.name,
        },
        role,
        domain: response?.data?.token?.project?.domain || null,
        scopedToken,
      }
    } catch (err) {
      // authentication failed
      console.error('changeProjectScope failed', generateApiErrorPayload(err).message)
      return {}
    }
  }

  authenticate = async (username, password, totp = '', domain = '') => {
    const body = totp
      ? constructAuthFromCredentialsTotp(username, password, totp, domain)
      : constructAuthFromCredentials(username, password, domain)
    try {
      const response = await this.client.rawPost<AuthToken>({
        url: this.tokensUrl,
        data: body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'authenticate',
        },
      })
      const { expires_at: expiresAt, issued_at: issuedAt } = response.data.token
      const domainDetails = response?.data?.token?.project?.domain || null
      const unscopedToken = response.headers['x-subject-token']
      this.client.unscopedToken = unscopedToken
      return { unscopedToken, username, expiresAt, issuedAt, domainDetails }
    } catch (err) {
      // authentication failed
      console.error('authenticate failed', generateApiErrorPayload(err).message)
      return {}
    }
  }

  authenticateSso = async () => {
    try {
      const response = await this.client.rawGet<any>({
        url: this.ssoLoginUrl.replace('domain_name', getIDPName()),
        options: {
          clsName: this.getClassName(),
          mthdName: 'authenticateSso',
        },
      })
      const { expires_at: expiresAt, issued_at: issuedAt, user } = response?.data?.token || {}
      const username = user.name || 'Unknown'

      const unscopedToken = response.headers['x-subject-token']
      this.client.unscopedToken = unscopedToken
      return { unscopedToken, username, expiresAt, issuedAt, ssoLogin: true }
    } catch (err) {
      // Redirect to login page if authentication fails
      location.href = loginUrl
      return {}
    }
  }

  getUnscopedTokenWithToken = async (token) => {
    const authBody = constructAuthFromToken(token)
    const body = {
      ...authBody,
      auth: {
        ...authBody.auth,
        scope: 'unscoped',
      },
    }
    try {
      const response = await this.client.rawPost<AuthToken>({
        url: this.tokensUrl,
        data: body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getUnscopedTokenWithToken',
        },
      })
      const username = response.data.token.user.name
      const { expires_at: expiresAt, issued_at: issuedAt } = response.data.token
      const unscopedToken = response.headers['x-subject-token']
      this.client.scopedToken = token
      this.client.unscopedToken = unscopedToken
      return { unscopedToken, username, expiresAt, issuedAt }
    } catch (err) {
      return {}
    }
  }

  renewToken = async (currUnscopedToken) => {
    const body = constructAuthFromToken(currUnscopedToken)
    try {
      const response = await this.client.rawPost<AuthToken>({
        url: this.tokensUrl,
        data: body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'renewToken',
        },
      })
      const { expires_at: expiresAt, issued_at: issuedAt } = response.data.token
      const unscopedToken = response.headers['x-subject-token']
      this.client.unscopedToken = unscopedToken
      return { unscopedToken, expiresAt, issuedAt }
    } catch (err) {
      // authentication failed
      return {}
    }
  }

  renewScopedToken = async (isSsoToken) => {
    const projectId = this.client.activeProjectId
    const body = isSsoToken
      ? constructAuthFromSaml(this.client.unscopedToken, projectId)
      : constructAuthFromToken(this.client.unscopedToken, projectId)
    try {
      const response = await this.client.rawPost<AuthToken>({
        url: this.tokensUrl,
        data: body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'renewScopedToken',
        },
      })
      const scopedToken = response.headers['x-subject-token']
      this.client.scopedToken = scopedToken
      return scopedToken
    } catch (err) {
      // authentication failed
      console.error('renewScopedToken failed', generateApiErrorPayload(err).message)
      return null
    }
  }

  getFeatures = async () => {
    try {
      const linksUrl = await this.getServiceEndpoint('regioninfo', 'public')
      const { links } = await this.client.basicGet<GetFeatureLinks>({
        endpoint: null,
        url: linksUrl,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getFeatures',
        },
      })
      const featuresUrl = links.features
      const timestamp = new Date().getTime()
      const features = await this.client.basicGet<GetFeatures>({
        endpoint: null,
        url: `${featuresUrl}?tag=${timestamp}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getFeatures/featuresUrl',
        },
      })
      return features
    } catch (err) {
      console.error('getFeatures failed', generateApiErrorPayload(err).message)
      return null
    }
  }

  getDownloadLinks = async () => {
    try {
      const linksUrl = await this.getServiceEndpoint('regioninfo', 'internal')
      const { links } = await this.client.basicGet<any>({
        endpoint: null,
        url: linksUrl,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getDownloadLinks',
        },
      })
      return links
    } catch (err) {
      console.error('getDownloadLinks failed', generateApiErrorPayload(err).message)
      return null
    }
  }

  // set cookie for accessing hostagent rpms
  resetCookie = async () => {
    return
    // try {
    //   return
    // const linksUrl = await this.getServiceEndpoint('regioninfo', 'public')
    // const { links } = await this.client.basicGet<GetFeatureLinks>({
    //   endpoint: null,
    //   url: linksUrl,
    //   options: {
    //     clsName: this.getClassName(),
    //     mthdName: 'resetCookie/links',
    //   },
    // })
    // const token2cookieUrl = links.token2cookie
    // const authHeaders = this.client.getAuthHeaders()
    // await this.client.rawGet<string>({
    //   endpoint: token2cookieUrl,
    //   url: '',
    //   config: {
    //     ...authHeaders,
    //     withCredentials: true,
    //   },
    //   options: {
    //     clsName: this.getClassName(),
    //     mthdName: 'resetCookie',
    //   },
    // })
    // } catch (err) {
    //   console.warn('Setting session cookie for accessing hostagent rpms failed')
    // }
  }

  @trackApiMethodMetadata({ url: '/v3/regions', type: 'GET' })
  getRegions = async () => {
    const data = await this.client.basicGet<GetRegions>({
      url: this.regionsUrl,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getRegions',
      },
    })
    return data.regions
  };

  // Allow programmatic access
  [DataKeys.Regions] = {
    list: async () => {
      return this.getRegions()
    },
  }

  @trackApiMethodMetadata({ url: '/v3/auth/catalog', type: 'GET' })
  getServiceCatalog = async (refresh = false) => {
    if (this.client.serviceCatalog && !refresh) {
      return this.client.serviceCatalog
    }
    const data = await this.client.basicGet<GetServiceCatalog>({
      url: this.catalogUrl,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getServiceCatalog',
      },
    })
    this.client.serviceCatalog = data.catalog
    return data.catalog
  }

  @trackApiMethodMetadata({ url: '/v3/endpoints', type: 'GET' })
  getEndpoints = async () => {
    const data = await this.client.basicGet<any>({
      url: this.endpointsUrl,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getEndpoints',
      },
    })
    this.client.endpoints = data.endpoints
    return data.endpoints
  }

  @trackApiMethodMetadata({ url: '/v3/endpoints', type: 'PATCH' })
  updateImageAdminEndpoint = async (id: string, body: any) => {
    return this.client.basicPatch<any>({
      url: `${this.endpointsUrl}/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateImageAdminEndpoint',
      },
    })
  }

  getActiveRegion = async () => {
    const appStore = store.getState()
    const username = appStore?.session?.username
    const prevRegion = getUserPrefsBucket(appStore?.[preferencesStoreKey], username)?.root
      ?.currentRegion
    const catalog = await this.getServiceCatalog()
    const { activeRegion, serviceCatalog = catalog } = this.client
    const servicesByRegion = groupByRegion(serviceCatalog)
    const regionKeys = Object.keys(servicesByRegion)
    const calcCurrentRegion = regionKeys.find((region) => {
      return servicesByRegion[region]?.resmgr?.public?.url?.includes(window.location.host)
    })
    return activeRegion || prevRegion || calcCurrentRegion
  }

  getServicesForActiveRegion = async (refresh = false): Promise<ServicesByName> => {
    const catalog = isSystemUser()
      ? this.client.serviceCatalog
      : await this.getServiceCatalog(refresh)
    const { serviceCatalog = catalog } = this.client
    const servicesByRegion = groupByRegion(serviceCatalog)
    const currentRegion = await this.getActiveRegion()
    if (!currentRegion || !Object.hasOwn(servicesByRegion, currentRegion)) {
      // Just assume the first region we come across if there isn't one set.
      return pipe<ServicesByRegion, ServicesByName[], ServicesByName>(
        values,
        head,
      )(servicesByRegion)
    }
    return servicesByRegion[currentRegion]
  }

  getServicesEndpointForTypeAndRegion = async (serviceName, type, region) => {
    const catalog = await this.getServiceCatalog()
    const { serviceCatalog = catalog } = this.client
    const allEnpoints =
      serviceCatalog.find((catalogObject) => catalogObject.name === serviceName)?.endpoints || []
    const serviceEndpoint = allEnpoints.find(
      (endpoint) => endpoint.region_id == region && endpoint.interface === type,
    )?.url

    return serviceEndpoint || ''
  }

  getServiceEndpoint = async (serviceName, type, refresh = false) => {
    const services = await this.getServicesForActiveRegion(refresh)
    const endpoint = pathStr(`${serviceName}.${type}.url`, services)
    if (!endpoint) {
      // TODO: handle loading these more gracefully
      // throw new ApiError(`${capitalizeString(serviceName)} endpoint not available in active region`)
      return ''
    }
    return endpoint
  }

  @trackApiMethodMetadata({ url: '/v3/credentials', type: 'GET' })
  getCredentials = async () => {
    const data = await this.client.basicGet<GetCredentials>({
      url: this.credentialsUrl,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getCredentials',
      },
    })
    return data.credentials
  }

  @trackApiMethodMetadata({ url: '/v3/credentials', type: 'POST' })
  addCredential = async (params) => {
    const body = {
      credential: params,
    }
    const data = await this.client.basicPost<any>({
      url: this.credentialsUrl,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addCredential',
      },
    })
    return data.credential
  }

  @trackApiMethodMetadata({
    url: '/v3/credentials/:credentialId',
    type: 'DELETE',
    params: ['credentialId'],
  })
  deleteCredential = async (credentialId) => {
    try {
      await this.client.basicDelete<any>({
        url: `${this.credentialsUrl}/${credentialId}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteCredential',
        },
      })
      return credentialId
    } catch (err) {
      throw new ApiError('Unable to delete non-existent credential')
    }
  }

  @trackApiMethodMetadata({ url: '/v3/users/:userId', type: 'GET', params: ['userId'] })
  getUser = async (id) => {
    const data = await this.client.basicGet<any>({
      url: `${this.usersUrl}/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getUser',
      },
    })
    return data.user
  }

  @trackApiMethodMetadata({ url: '/v3/users', type: 'GET' })
  getUsers = async ({ domainId = undefined }) => {
    const data = await this.client.basicGet<GetUsers>({
      url: addDomainIdToURL(this.usersUrl, domainId),
      options: {
        clsName: this.getClassName(),
        mthdName: 'getUsers',
      },
    })
    // Filter service/internal users
    return data.users?.filter(filterValidUsers)
  }

  @trackApiMethodMetadata({ url: '/v3/users', type: 'POST' })
  createUser = async (params) => {
    const body = { user: params }
    const data = await this.client.basicPost<any>({
      url: this.usersUrl,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createUser',
      },
    })
    return data.user
  }

  @trackApiMethodMetadata({ url: '/v3/users/:userId', type: 'PATCH', params: ['userId'] })
  updateUser = async (id, params) => {
    const body = { user: params }
    const url = `${this.usersUrl}/${id}`
    const data = await this.client.basicPatch<UpdateUser>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateUser',
      },
    })
    return data.user
  }

  @trackApiMethodMetadata({ url: '/v3/users/:userId/password', type: 'POST', params: ['userId'] })
  updateUserPassword = async (id, params) => {
    const body = { user: params }
    const url = `${this.usersUrl}/${id}/password`
    const data = await this.client.basicPost<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateUserPassword',
      },
    })
    return data.user
  }

  @trackApiMethodMetadata({ url: '/v3/users/:userId', type: 'DELETE', params: ['userId'] })
  deleteUser = async (userId) => {
    try {
      await this.client.basicDelete<any>({
        url: `${this.usersUrl}/${userId}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'deleteUser',
        },
      })
      return userId
    } catch (err) {
      throw new ApiError('Unable to delete non-existant user')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/users/:userId/application_credentials',
    type: 'GET',
    params: ['userId'],
  })
  getAllApplicationCredentials = async ({ userId }) => {
    const data = await this.client.basicGet<GetApplicationCredentials>({
      url: this.applicationCredentialsUrl.replace(':userId', userId),
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllApplicationCredentials',
      },
    })
    return data.application_credentials
  }

  @trackApiMethodMetadata({
    url: '/v3/users/:userId/application_credentials',
    type: 'GET',
    params: ['userId'],
  })
  getApplicationCredential = async ({ userId, id }) => {
    try {
      const data = await this.client.basicGet<any>({
        url: `${this.applicationCredentialsUrl.replace(':userId', userId)}/${id}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getApplicationCredential',
        },
      })
      return data
    } catch (err) {
      return null
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/users/:userId/application_credentials',
    type: 'GET',
    params: ['userId'],
  })
  deleteApplicationCredential = async ({ userId, id }) => {
    try {
      const data = await this.client.basicDelete<GetApplicationCredentials>({
        url: `${this.applicationCredentialsUrl.replace(':userId', userId)}/${id}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getAllApplicationCredentials',
        },
      })
      return data
    } catch (err) {
      throw new ApiError('Unable to delete application credential')
    }
  }

  @trackApiMethodMetadata({
    url: '/v3/users/:userId/application_credentials',
    type: 'POST',
    params: ['userId'],
  })
  createApplicationCredentials = async ({ userId, body }) => {
    try {
      const data = await this.client.basicPost<any>({
        url: this.applicationCredentialsUrl.replace(':userId', userId),
        body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'createApplicationCredentials',
        },
      })
      return data.application_credential
    } catch (err) {
      throw err
    }
  }

  // CQRS auth methods
  getUserPermissions = async () => {
    return this.client.basicGet<UserPermissionsMap>({
      url: '/user-actions',
      endpoint: this.baseRbacEndpoint,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getUserPermissions',
      },
    })
  }

  getPermissionsList = async () => {
    const data = await this.client.basicGet<GetPermissionsListResponse>({
      url: '/v1/permissions',
      endpoint: this.baseRbacEndpoint,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getPermissionsList',
      },
    })
    return data.permissions
  }

  getCapabilitiesList = async () => {
    const data = await this.client.basicGet<GetCapabilitiesListResponse>({
      url: '/v1/capabilities',
      endpoint: this.baseRbacEndpoint,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getCapabilitiesList',
      },
    })
    return data.capabilities
  }

  createRole = async ({ body }) => {
    const response = await this.client.basicPost<GetCqrsRoleResponse>({
      url: `/v1/roles`,
      endpoint: this.baseRbacEndpoint,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createCustomRole',
      },
    })
    return response.role
  }

  updateRole = async ({ id, body }): Promise<CqrsRole> => {
    const response = await this.client.basicPut<CqrsRole>({
      url: `/v1/roles/${id}`,
      endpoint: this.baseRbacEndpoint,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateCustomRole',
      },
    })
    return response
  }

  getCqrsRole = async ({ id }) => {
    const data = await this.client.basicGet<GetCqrsRoleResponse>({
      url: `/v1/roles/${id}`,
      endpoint: this.baseRbacEndpoint,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getCqrsRole',
      },
    })
    return data.role
  }
}

export default Keystone
